import { ConvertDates, FirestoreTimestamp } from '@griply/types';

// Cross-realm-safe Date check. `instanceof Date` is bound to the realm that
// declared the Date class — Firestore's persistent IndexedDB cache surfaces
// Date instances created in a different JS realm (worker / iframe context),
// where `instanceof Date` returns false even though the value has Date.prototype.
// `Object.prototype.toString` returns '[object Date]' regardless of realm.
const isDate = (val: unknown): val is Date =>
  Object.prototype.toString.call(val) === '[object Date]';

const isRecord = (val: unknown): val is Record<string, unknown> =>
  val !== null &&
  typeof val === 'object' &&
  !Array.isArray(val) &&
  !isDate(val);

export const dateToFirestoreTimestamp = <T, TTimestamp = FirestoreTimestamp>(
  obj: T,
  timestampFromDate: (date: Date) => TTimestamp,
): ConvertDates<T, TTimestamp> => {
  // Cast: TS can't track runtime narrowing (isDate/Array.isArray/isRecord) through ConvertDates<T, TTimestamp>.
  return convert(obj, timestampFromDate) as ConvertDates<T, TTimestamp>;
};

const convert = <TTimestamp>(
  obj: unknown,
  timestampFromDate: (date: Date) => TTimestamp,
): unknown => {
  if (isDate(obj)) {
    return timestampFromDate(obj);
  }

  if (Array.isArray(obj)) {
    return obj
      .map((item) => convert(item, timestampFromDate))
      .filter((item) => item !== undefined);
  }

  if (isRecord(obj)) {
    const transformed: Record<string, unknown> = {};

    for (const key of Object.keys(obj)) {
      const val = convert(obj[key], timestampFromDate);

      if (val === undefined) {
        continue;
      }

      transformed[key] = val;
    }

    return transformed;
  }

  return obj;
};
